import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Airline master (Masters → Airlines). Identity is the unique IATA code (no
 * `code`/`legacyId` columns, unlike the other masters). ICAO code is an
 * optional secondary unique identifier. See ADR-0016 / ADR-0020.
 */

/** IATA airline code: 2-3 alphanumerics, normalized upper (e.g. "AI", "6E"). */
export const IataAirlineCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9]{2,3}$/, 'IATA code is 2-3 letters/digits')
  .transform((s) => s.toUpperCase());

/** ICAO airline code: 3 letters, normalized upper (e.g. "AIC", "UAE"). */
export const IcaoAirlineCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z]{3}$/, 'ICAO code is exactly 3 letters')
  .transform((s) => s.toUpperCase());

export const AirlineListItemSchema = z.object({
  id: UuidSchema,
  iataCode: z.string(),
  icaoCode: z.string().nullable(),
  name: z.string(),
  /** Loose FK to `countries` (no Prisma relation); resolve label client-side. */
  countryId: UuidSchema.nullable(),
  logoUrl: z.string().nullable(),
  isLowCost: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type AirlineListItem = z.infer<typeof AirlineListItemSchema>;

export const AirlineListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  isLowCost: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type AirlineListQuery = z.infer<typeof AirlineListQuerySchema>;

export const AirlineListResponseSchema = paginatedResponseSchema(AirlineListItemSchema);
export type AirlineListResponse = z.infer<typeof AirlineListResponseSchema>;

export const AirlineDetailSchema = AirlineListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type AirlineDetail = z.infer<typeof AirlineDetailSchema>;

const AirlineCoreFieldsSchema = z.object({
  iataCode: IataAirlineCodeSchema,
  icaoCode: IcaoAirlineCodeSchema.nullable().optional(),
  name: z.string().trim().min(1, 'Name is required').max(200),
  countryId: UuidSchema.nullable().optional(),
  logoUrl: z.string().trim().url('Enter a valid URL').max(500).nullable().optional(),
  isLowCost: z.boolean().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateAirlineRequestSchema = AirlineCoreFieldsSchema;
export type CreateAirlineRequest = z.infer<typeof CreateAirlineRequestSchema>;

export const UpdateAirlineRequestSchema = AirlineCoreFieldsSchema.partial();
export type UpdateAirlineRequest = z.infer<typeof UpdateAirlineRequestSchema>;
